import { useParams, useNavigate, Link } from "react-router-dom";
import {
  useGetStudentByIdQuery,
  useUpdateStudentMutation,
  type Student,
} from "@/redux/slices/studentApi";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  User,
  Mail,
  Phone,
  School,
  MapPin,
  Calendar,
  CheckCircle2,
  XCircle,
  MessagesSquare,
  BookOpen,
  ShoppingBag,
  KeyRound,
  ShieldCheck,
  AlertCircle,
  Loader2,
  ExternalLink,
  Edit2,
  RefreshCw
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export default function StudentDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: studentRes, isLoading, isError, refetch } = useGetStudentByIdQuery(id as string, {
    skip: !id,
  });

  const [updateStudent, { isLoading: isUpdating }] = useUpdateStudentMutation();

  const student = studentRes?.data;

  const handleToggleStatus = async () => {
    if (!student) return;
    const nextStatus = student.status === "active" ? "inactive" : "active";
    try {
      await updateStudent({
        id: student.id,
        body: { status: nextStatus },
      }).unwrap();
      toast.success(`Student status updated to ${nextStatus}!`);
      refetch();
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to update status");
    }
  };

  const handleToggleSupportKey = async () => {
    if (!student) return;
    try {
      await updateStudent({
        id: student.id,
        body: { supportKey: !student.supportKey },
      }).unwrap();
      toast.success(
        student.supportKey
          ? "Support key access disabled"
          : "Support key access enabled for student"
      );
      refetch();
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to update support key");
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center space-y-3">
        <Loader2 className="h-9 w-9 animate-spin text-primary" />
        <p className="text-xs text-muted-foreground font-semibold">Loading student profile...</p>
      </div>
    );
  }

  if (isError || !student) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center space-y-4 max-w-md mx-auto text-center px-4">
        <div className="h-16 w-16 rounded-2xl bg-destructive/10 text-destructive flex items-center justify-center">
          <AlertCircle className="h-8 w-8" />
        </div>
        <h2 className="text-xl font-bold text-foreground">Student Not Found</h2>
        <p className="text-xs text-muted-foreground">
          The requested student record does not exist or you do not have permission to access it.
        </p>
        <Button onClick={() => navigate(-1)} variant="outline" className="rounded-xl font-bold text-xs">
          <ArrowLeft className="mr-2 h-4 w-4" /> Go Back
        </Button>
      </div>
    );
  }

  const initials = student.name
    ? student.name
        .split(" ")
        .map((p) => p[0])
        .slice(0, 2)
        .join("")
        .toUpperCase()
    : "ST";

  return (
    <div className="min-h-screen bg-muted/20 pb-16">
      {/* Top Navigation Banner */}
      <div className="border-b bg-card/80 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate(-1)}
              variant="outline"
              size="sm"
              className="rounded-xl h-9 px-3 gap-1.5 text-xs font-semibold"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-base font-bold text-foreground">{student.name}</h1>
                <Badge
                  variant={student.status === "active" ? "default" : "secondary"}
                  className={cn(
                    "rounded-md text-[10px] font-bold uppercase",
                    student.status === "active"
                      ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30"
                      : "bg-muted text-muted-foreground"
                  )}
                >
                  {student.status}
                </Badge>
              </div>
              <p className="text-[11px] text-muted-foreground">{student.mobile || student.email}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              onClick={() => refetch()}
              variant="outline"
              size="sm"
              className="rounded-xl h-9 text-xs font-semibold"
            >
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Refresh
            </Button>
            <Button
              onClick={handleToggleStatus}
              disabled={isUpdating}
              variant={student.status === "active" ? "outline" : "default"}
              size="sm"
              className="rounded-xl h-9 text-xs font-bold"
            >
              {student.status === "active" ? (
                <>
                  <XCircle className="mr-1.5 h-3.5 w-3.5 text-destructive" /> Deactivate Student
                </>
              ) : (
                <>
                  <CheckCircle2 className="mr-1.5 h-3.5 w-3.5 text-emerald-500" /> Activate Student
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 space-y-6">
        {/* Profile Card Header */}
        <Card className="rounded-2xl border shadow-sm overflow-hidden bg-card">
          <div className="p-6 sm:p-8 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="flex items-start sm:items-center gap-4">
              <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-2xl bg-primary/10 text-primary flex items-center justify-center font-black text-xl sm:text-2xl shadow-inner shrink-0">
                {initials}
              </div>
              <div className="space-y-1">
                <h2 className="text-xl sm:text-2xl font-black text-foreground">{student.name}</h2>
                <div className="flex flex-wrap items-center gap-2 pt-1 text-xs text-muted-foreground">
                  {student.email && (
                    <a
                      href={`mailto:${student.email}`}
                      className="flex items-center gap-1.5 hover:text-primary transition-colors"
                    >
                      <Mail className="h-3.5 w-3.5 text-primary" /> {student.email}
                    </a>
                  )}
                  {student.mobile && (
                    <a
                      href={`tel:${student.mobile}`}
                      className="flex items-center gap-1.5 hover:text-primary transition-colors"
                    >
                      <Phone className="h-3.5 w-3.5 text-emerald-500" /> {student.mobile}
                    </a>
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant={student.supportKey ? "default" : "outline"}
                size="sm"
                onClick={handleToggleSupportKey}
                disabled={isUpdating}
                className="rounded-xl h-9 text-xs font-bold gap-1.5"
              >
                <KeyRound className="h-3.5 w-3.5" />
                {student.supportKey ? "Support Key: Enabled" : "Enable Support Key"}
              </Button>
            </div>
          </div>
        </Card>

        {/* 2-Column Info Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: Academic & Personal Details */}
          <div className="space-y-6 lg:col-span-1">
            <Card className="rounded-2xl border shadow-sm">
              <CardHeader className="pb-3 border-b">
                <CardTitle className="text-sm font-bold flex items-center gap-2 text-foreground">
                  <User className="h-4 w-4 text-primary" /> Student Profile Info
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-3.5 text-xs">
                <div>
                  <span className="text-muted-foreground block text-[11px]">Academic Cohort Year</span>
                  <span className="font-bold text-foreground text-sm">
                    {student.academicYear?.name || "Not Assigned"}
                  </span>
                </div>

                <div>
                  <span className="text-muted-foreground block text-[11px]">College / Institute</span>
                  <div className="flex items-center gap-1.5 font-semibold text-foreground mt-0.5">
                    <School className="h-3.5 w-3.5 text-primary shrink-0" />
                    <span>{student.collegeName || "Not Provided"}</span>
                  </div>
                </div>

                <div>
                  <span className="text-muted-foreground block text-[11px]">State / Region</span>
                  <div className="flex items-center gap-1.5 font-semibold text-foreground mt-0.5">
                    <MapPin className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                    <span>{student.state || "Not Provided"}</span>
                  </div>
                </div>

                <div>
                  <span className="text-muted-foreground block text-[11px]">Referral Code</span>
                  <span className="font-mono font-bold text-foreground">
                    {student.referralCode || "None"}
                  </span>
                </div>

                <div>
                  <span className="text-muted-foreground block text-[11px]">Enrollment Date</span>
                  <div className="flex items-center gap-1.5 font-semibold text-foreground mt-0.5">
                    <Calendar className="h-3.5 w-3.5 text-primary shrink-0" />
                    <span>
                      {student.createdAt
                        ? format(new Date(student.createdAt), "dd MMM yyyy, hh:mm a")
                        : "N/A"}
                    </span>
                  </div>
                </div>

                <div>
                  <span className="text-muted-foreground block text-[11px]">Account ID (UUID)</span>
                  <span className="font-mono text-[10px] text-muted-foreground break-all">
                    {student.id}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column: Chat Groups & Chapter Purchases */}
          <div className="space-y-6 lg:col-span-2">
            {/* Chat Groups Membership Card */}
            <Card className="rounded-2xl border shadow-sm">
              <CardHeader className="pb-3 border-b flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-sm font-bold flex items-center gap-2 text-foreground">
                    <MessagesSquare className="h-4 w-4 text-primary" /> Active Chat Groups
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Cohorts and discussion rooms this student is currently participating in.
                  </CardDescription>
                </div>
                <Badge variant="secondary" className="font-bold text-[10px]">
                  {student.chatGroups?.length || 0} Groups
                </Badge>
              </CardHeader>
              <CardContent className="p-4">
                {!student.chatGroups || student.chatGroups.length === 0 ? (
                  <div className="p-6 text-center text-xs text-muted-foreground">
                    <MessagesSquare className="h-7 w-7 mx-auto mb-2 opacity-40" />
                    This student is not currently enrolled in any active chat groups.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {student.chatGroups.map((g) => (
                      <div
                        key={g.groupId}
                        onClick={() => navigate(`/chat-groups/${g.groupId}`)}
                        className="p-3.5 rounded-xl border bg-card hover:bg-muted/40 transition-all cursor-pointer flex flex-col justify-between space-y-2 group"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <div className="h-8 w-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                              <MessagesSquare className="h-4 w-4" />
                            </div>
                            <div>
                              <p className="font-bold text-xs text-foreground group-hover:text-primary transition-colors line-clamp-1">
                                {g.group?.name || "Group"}
                              </p>
                              <p className="text-[10px] text-muted-foreground">
                                {g.group?.educationYear}
                              </p>
                            </div>
                          </div>
                          <Badge
                            variant={g.status === "active" ? "default" : "secondary"}
                            className="text-[9px] px-1.5 py-0 rounded"
                          >
                            {g.status}
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between text-[10px] text-muted-foreground pt-1 border-t">
                          <span>
                            Joined: {g.joinedAt ? format(new Date(g.joinedAt), "dd MMM yyyy") : "N/A"}
                          </span>
                          <span className="flex items-center gap-1 text-primary font-semibold">
                            Open Chat <ExternalLink className="h-2.5 w-2.5" />
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Chapter Purchases / Enrollments Card */}
            <Card className="rounded-2xl border shadow-sm">
              <CardHeader className="pb-3 border-b flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-sm font-bold flex items-center gap-2 text-foreground">
                    <BookOpen className="h-4 w-4 text-emerald-500" /> Chapter-Wise Lifetime Purchases
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Chapters and learning content permanently unlocked by this student.
                  </CardDescription>
                </div>
                <Badge variant="secondary" className="font-bold text-[10px]">
                  {student.purchases?.length || 0} Chapters Unlocked
                </Badge>
              </CardHeader>
              <CardContent className="p-4">
                {!student.purchases || student.purchases.length === 0 ? (
                  <div className="p-6 text-center text-xs text-muted-foreground">
                    <BookOpen className="h-7 w-7 mx-auto mb-2 opacity-40 text-emerald-500" />
                    No individual chapter purchases recorded yet for this student.
                  </div>
                ) : (
                  <div className="divide-y border rounded-xl overflow-hidden bg-card">
                    {student.purchases.map((p) => (
                      <div
                        key={p.id}
                        className="p-3 flex items-center justify-between gap-4 text-xs hover:bg-muted/30 transition-colors"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="h-8 w-8 rounded-lg bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0">
                            <BookOpen className="h-4 w-4" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-bold text-foreground truncate">
                              {p.chapter?.name || "Chapter Material"}
                            </p>
                            <p className="text-[10px] text-muted-foreground truncate">
                              Subject: {p.chapter?.subject?.name || "General Subject"}
                            </p>
                          </div>
                        </div>

                        <div className="text-right shrink-0">
                          <p className="font-bold text-foreground">
                            {p.amount !== undefined && p.amount !== null ? `₹${p.amount}` : "Active"}
                          </p>
                          <p className="text-[10px] text-muted-foreground">
                            {p.createdAt ? format(new Date(p.createdAt), "dd MMM yyyy") : ""}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}

